import { FC, useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button.tsx";
import { useClient } from "@/hooks/useClient.ts";
import { parseQuiz } from "@/lib/utils.ts";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group.tsx";
import { Label } from "@/components/ui/label.tsx";
import { z } from "zod";
import { TFunction } from "i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form.tsx";
import { useTranslation } from "@/i18n";

const createFormSchema = (t: TFunction) =>
  z.object({
    quizAnswer: z.string({
      required_error: t("validation.quizAnswerRequired"),
    }),
  });

type FormValues = z.infer<ReturnType<typeof createFormSchema>>;

const QuizForm: FC<{
  onSuccess: () => void;
  setError: (error: string) => void;
}> = ({ onSuccess, setError }) => {
  const { t } = useTranslation();
  const formSchema = useMemo(() => createFormSchema(t), [t]);
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
  });

  const [quiz, setQuiz] = useState<{
    question: string;
    answer: string | null;
    options: string[];
  }>();

  const client = useClient();

  useEffect(() => {
    try {
      const parsedQuiz = parseQuiz(client!.quiz);
      if (
        parsedQuiz.options.length &&
        parsedQuiz.answer &&
        parsedQuiz.options.includes(parsedQuiz.answer)
      ) {
        setQuiz(parsedQuiz);
      } else {
        onSuccess();
      }
    } catch (e) {
      console.error("Failed to parse quiz", e);
      onSuccess(); //Allow user to register anyway
    }
  }, []);

  const onSubmit = (data: FormValues) => {
    if (data.quizAnswer === quiz!.answer) {
      setError("");
      onSuccess();
    } else {
      setError(t("auth.quiz.wrongAnswer"));
    }
  };

  if (!quiz) return null;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <div className="grid gap-6">
          <FormField
            control={form.control}
            name="quizAnswer"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{quiz.question}</FormLabel>
                <FormControl>
                  <div className="grid gap-6">
                    <RadioGroup
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                      className="flex flex-col space-y-1"
                    >
                      {quiz.options.map((option, index) => (
                        <div className="flex items-center space-x-2">
                          <FormControl>
                            <RadioGroupItem
                              value={option}
                              id={`option-${index}`}
                            />
                          </FormControl>
                          <Label htmlFor={`option-${index}`}>{option}</Label>
                        </div>
                      ))}
                    </RadioGroup>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button type="submit" className="w-full">
            {t("auth.quiz.submit")}
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default QuizForm;
